import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  InfoButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/subject-outcomes/SubjectOutcomesAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { SubjectOutcomesFilter } from "./subject-outcomes-filter/SubjectOutcomesFilter";
import { SubjectOutcomesTable } from "./subject-outcomes-table/SubjectOutcomesTable";
import { useSubjectOutcomesUIContext } from "./SubjectOutcomesUIContext";

export function SubjectOutcomesCard() {
  // SubjectOutcomes UI Context
  const subjectOutcomesUIContext = useSubjectOutcomesUIContext();
  const subjectOutcomesUIProps = useMemo(() => {
    return {
      ids: subjectOutcomesUIContext.ids,
      setIds: subjectOutcomesUIContext.setIds,
      subjectId: subjectOutcomesUIContext.subjectId,
      queryParams: subjectOutcomesUIContext.queryParams,
      openCourseSubjectListPage: subjectOutcomesUIContext.openCourseSubjectListPage,
      openNewSubjectOutcomePage: subjectOutcomesUIContext.openNewSubjectOutcomePage,
    };
  }, [subjectOutcomesUIContext]);

  // Getting curret state of Subject Outcome list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.subjectOutcomes }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, subject, course, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Subject Outcomes Redux state
  const dispatch = useDispatch();
  
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
  
  /** Belli Bir Konu Kazanımı Listesini Excele Aktarır */
  const exportOutcomesToExcel = () => {
    dispatch(actions.getAllSubjectOutcomes(subjectOutcomesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [45, 60];
      const columns = ["Kazanım", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.outcome, entity.explanation]));
      const filename = course + " " + subject.replaceAll(".", ",") + " Konu Kazanımı Listesi";
      const sheetname = filename.length <= 31 ? filename : "Konu Kazanımı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Konu Kazanımı Listesini PDF e Aktarır */
  const exportOutcomesToPDF = () => {
    dispatch(actions.getAllSubjectOutcomes(subjectOutcomesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Kazanım",
          rows: entities.map(entity => entity.outcome)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.explanation)
        }
      ];
  
      const filename = course + " " + subject.replaceAll(".", ",") + " Konu Kazanımı Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Konu Kazanımı Listesini Yazdırma Penceresini Açar */
  const printOutcomes = () => {
    dispatch(actions.getAllSubjectOutcomes(subjectOutcomesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = course + " " + subject + " Konu Kazanımı Listesi";
      const properties = ["Kazanım", "Açıklaması"];
  
      entities.forEach(entity => {
        data.push({
          "Kazanım": entity.outcome,
          "Açıklaması": entity.explanation
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Konu Kazanımı Listesini Panoya Kopyalar */
  const copyOutcomes = () => {
    dispatch(actions.getAllSubjectOutcomes(subjectOutcomesUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Kazanım", "Açıklaması"];
      const header = course + " " + subject + " Konu Kazanımı Listesi";
      entities.forEach(entity => rows.push([entity.outcome, entity.explanation]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Konu Kazanımı Listesini Siler */
  const deleteSubjectOutcomes = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }
  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteSubjectOutcomes(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        subjectOutcomesUIProps.setIds([]);
        dispatch(actions.getSubjectOutcomes(subjectOutcomesUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>
                  Ders: <span style={{color:"red"}}>{course}</span>                  
                  <br /><br />
                  Konu: <span style={{color:"red"}}>{subject}</span>
                  <br /><br />
                  Konu Kazanımı Listesi
                  {
                    subjectOutcomesUIProps.ids.length > 0 ?
                    <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{subjectOutcomesUIProps.ids.length}</span>]</> : 
                    ""
                  }
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        <CardHeaderToolbar>
          <InfoButton
            path="Navigation/Arrow-left"
            title="Ders Konusu Listesi"
            method={subjectOutcomesUIProps.openCourseSubjectListPage}
          />
          {
            isExportationAuthorized &&
            <ExportButton
              exportToExcel={exportOutcomesToExcel}
              exportToPDF={exportOutcomesToPDF}
              print={printOutcomes}
              copy={copyOutcomes}
            />
          }
          {
            isAdditionAuthorized &&
            <FormButton 
              path="Navigation/Plus"
              title="Yeni Konu Kazanımı"
              method={subjectOutcomesUIProps.openNewSubjectOutcomePage}
            />
          }              
          {
            isDeletionAuthorized && 
            <DeleteButton 
              title="Seçilenleri Sil"
              method={() => deleteSubjectOutcomes(subjectOutcomesUIProps.ids)}
            />
          }
        </CardHeaderToolbar>
      </CardHeader>
      <CardBody>
        <SubjectOutcomesFilter />
        <SubjectOutcomesTable
          deleteSubjectOutcomes={deleteSubjectOutcomes}
        />
      </CardBody>
    </Card>
  );
}